import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  getOrganizations,
  createOrganization,
  deleteOrganization,
} from "../api/organizations";
import PhoneInput from "../components/PhoneInput";

// ── Plan & display config ─────────────────────────────────────────────────────

const PLAN_LABELS = {
  FREE: "Free", FREE_IN: "Free",
  STANDARD: "Standard", STANDARD_IN: "Standard (IN)",
  BUSINESS: "Business", BUSINESS_IN: "Business (IN)",
  BUSINESS_PRO: "Business Pro",
  GROUP_STARTER: "Group Starter", GROUP_STARTER_IN: "Group Starter (IN)",
  GROUP_PRO: "Group Pro", GROUP_PRO_IN: "Group Pro (IN)",
  ENTERPRISE_HOLDING: "Enterprise Holding",
};

const PLAN_COLORS = {
  FREE: "bg-gray-100 text-gray-700",
  STANDARD: "bg-blue-100 text-blue-700", STANDARD_IN: "bg-blue-100 text-blue-700",
  BUSINESS: "bg-purple-100 text-purple-700", BUSINESS_IN: "bg-purple-100 text-purple-700",
  BUSINESS_PRO: "bg-indigo-100 text-indigo-700",
  GROUP_STARTER: "bg-emerald-100 text-emerald-700", GROUP_STARTER_IN: "bg-emerald-100 text-emerald-700",
  GROUP_PRO: "bg-teal-100 text-teal-700", GROUP_PRO_IN: "bg-teal-100 text-teal-700",
  ENTERPRISE_HOLDING: "bg-orange-100 text-orange-700",
};

/** All plans the admin can assign. `idIN` = India variant. `custom` = requires custom pricing. */
const PLANS_CONFIG = [
  // ── Individual ──────────────────────────────────────────────────────────
  {
    id: "FREE", idIN: "FREE_IN", name: "Free", section: "individual",
    custom: false, featured: false,
    monthly: 0, yearly: 0, monthlyIN: 0, yearlyIN: 0,
    signRequests: 3, templates: 2, companies: 1,
    features: ["3 sign requests / mo", "2 templates", "1 user"],
  },
  {
    id: "STANDARD", idIN: "STANDARD_IN", name: "Standard", section: "individual",
    custom: false, featured: false,
    monthly: 10, yearly: 9, monthlyIN: 499, yearlyIN: 449,
    signRequests: 10, templates: 5, companies: 1,
    features: ["10 sign requests / mo", "5 templates", "Bulk send"],
  },
  {
    id: "BUSINESS", idIN: "BUSINESS_IN", name: "Business", section: "individual",
    custom: false, featured: true,
    monthly: 30, yearly: 27, monthlyIN: 999, yearlyIN: 899,
    signRequests: 30, templates: -1, companies: 1,
    features: ["30 sign requests / mo", "Unlimited templates", "OTP verification", "Role control"],
  },
  {
    id: "BUSINESS_PRO", idIN: null, name: "Business Pro", section: "individual",
    custom: true, featured: false,
    features: ["Custom sign requests", "Custom pricing", "Dedicated support"],
    companies: 1,
  },
  // ── Group ────────────────────────────────────────────────────────────────
  {
    id: "GROUP_STARTER", idIN: "GROUP_STARTER_IN", name: "Group Starter", section: "group",
    custom: false, featured: false,
    monthly: 79, yearly: 69, monthlyIN: 3999, yearlyIN: 3499,
    signRequests: 300, templates: -1, companies: 3,
    features: ["Up to 3 companies", "300 sign requests / mo", "Unlimited templates"],
  },
  {
    id: "GROUP_PRO", idIN: "GROUP_PRO_IN", name: "Group Pro", section: "group",
    custom: false, featured: true,
    monthly: 199, yearly: 179, monthlyIN: 9999, yearlyIN: 8999,
    signRequests: 1200, templates: -1, companies: 10,
    features: ["Up to 10 companies", "1200 sign requests / mo", "Dedicated support"],
  },
  {
    id: "ENTERPRISE_HOLDING", idIN: null, name: "Enterprise", section: "group",
    custom: true, featured: false,
    features: ["Unlimited companies", "Custom pricing", "Custom sign requests", "Full control"],
    companies: -1,
  },
];

/** Returns the effective planType to send to backend based on payment_country */
const effectivePlanId = (plan, payment_country) => {
  if (payment_country === "IN" && plan.idIN) return plan.idIN;
  return plan.id;
};

/** Returns the display price for a plan given billing cycle and country */
const displayPrice = (plan, cycle, isIN) => {
  if (plan.custom) return null;
  if (isIN) return cycle === "MONTHLY" ? plan.monthlyIN : plan.yearlyIN;
  return cycle === "MONTHLY" ? plan.monthly : plan.yearly;
};

// ── Step indicator ────────────────────────────────────────────────────────────
const StepIndicator = ({ current, total, labels }) => (
  <div className="flex items-center justify-center gap-0 mb-8">
    {Array.from({ length: total }).map((_, i) => {
      const step = i + 1;
      const done = step < current;
      const active = step === current;
      return (
        <React.Fragment key={step}>
          <div className="flex flex-col items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all ${
                done
                  ? "bg-primary border-primary text-white"
                  : active
                  ? "border-primary text-primary bg-white"
                  : "border-gray-300 text-gray-400 bg-white"
              }`}
            >
              {done ? "✓" : step}
            </div>
            <span className={`text-xs mt-1 font-medium ${active ? "text-primary" : done ? "text-gray-600" : "text-gray-400"}`}>
              {labels[i]}
            </span>
          </div>
          {i < total - 1 && (
            <div className={`h-0.5 w-16 mb-4 mx-1 ${done ? "bg-primary" : "bg-gray-200"}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ── Field component ───────────────────────────────────────────────────────────
const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const Input = ({ ...props }) => (
  <input
    {...props}
    className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${props.className || ""}`}
  />
);

// ── Create Organization Modal ─────────────────────────────────────────────────
const STEPS = ["Details", "Plan", "Review"];
const STEP_COUNT = 3;

const defaultForm = () => ({
  // Step 1
  orgName: "",
  memberName: "",
  memberEmail: "",
  memberPassword: "",
  payment_country: "US",
  // Step 2 — company
  companyName: "",
  phone: "",
  address: "",
  // Step 3 — plan
  planId: "BUSINESS",          // base plan id (key into PLANS_CONFIG)
  subscriptionType: "MONTHLY",
  monthlyPrice: "",
  yearlyPrice: "",
  // Full planMeta matching APP_PLANS structure (used only for custom plans)
  planMeta: {
    USER_CREATION:       -1,
    SIGN_REQUESTS:       30,
    TEMPLATE_CREATION:   -1,
    BULK_SEND:           true,
    REMINDERS:           true,
    MULTI_DEVICE_ACCESS: true,
    SEQUENTIAL_SIGNING:  true,
    ROLE_CONTROL:        true,
    ATTACHMENT_REQUESTS: true,
    DEDICATED_SUPPORT:   true,
    OTP_VERIFICATION:    true,
    ALLOWED_COMPANIES:   1,
    IS_GROUP_PLAN:       false,
  },
});

const CreateOrgModal = ({ onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(defaultForm());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const isIN = form.payment_country === "IN";
  const selectedPlan = PLANS_CONFIG.find((p) => p.id === form.planId) || PLANS_CONFIG[0];
  const isCustom = selectedPlan.custom;
  const effectivePlan = effectivePlanId(selectedPlan, form.payment_country);
  const isFreeLocal = effectivePlan === "FREE" || effectivePlan === "FREE_IN";
  const cycleLabel = form.subscriptionType === "MONTHLY" ? "month" : "year";
  const currencySymbol = isIN ? "₹" : "$";

  // Auto-populate planMeta defaults when plan changes
  const selectPlan = (planId) => {
    const plan = PLANS_CONFIG.find((p) => p.id === planId);
    if (!plan) return;
    const isEnterprise = planId === "ENTERPRISE_HOLDING";
    setForm((f) => ({
      ...f,
      planId,
      monthlyPrice: "",
      yearlyPrice: "",
      planMeta: {
        USER_CREATION:       -1,
        SIGN_REQUESTS:       plan.signRequests ?? 30,
        TEMPLATE_CREATION:   plan.templates   ?? -1,
        BULK_SEND:           true,
        REMINDERS:           true,
        MULTI_DEVICE_ACCESS: true,
        SEQUENTIAL_SIGNING:  true,
        ROLE_CONTROL:        true,
        ATTACHMENT_REQUESTS: true,
        DEDICATED_SUPPORT:   true,
        OTP_VERIFICATION:    true,
        ALLOWED_COMPANIES:   isEnterprise ? -1 : (plan.companies ?? 1),
        IS_GROUP_PLAN:       isEnterprise,
      },
    }));
  };

  const setField = (key, value) =>
    setForm((f) => ({ ...f, [key]: value }));

  const setPlanMetaField = (key, value) =>
    setForm((f) => ({ ...f, planMeta: { ...f.planMeta, [key]: value } }));

  // ── Validation per step ──────────────────────────────────────────────────
  const validateStep = (s) => {
    const errs = {};
    if (s === 1) {
      if (!form.memberName.trim()) errs.memberName = "Required";
      if (!form.memberEmail.trim()) errs.memberEmail = "Required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.memberEmail)) errs.memberEmail = "Invalid email";
      if (!form.memberPassword.trim()) errs.memberPassword = "Required";
      else if (form.memberPassword.length < 6) errs.memberPassword = "Min 6 characters";
    }
    if (s === 2) {
      if (!form.companyName.trim()) errs.companyName = "Required";
    }
    if (s === 3) {
      // All paid plans need a billing cycle
      const effId = effectivePlanId(selectedPlan, form.payment_country);
      const planIsFree = effId === "FREE" || effId === "FREE_IN";
      if (!planIsFree && !form.subscriptionType) {
        errs.subscriptionType = "Please select a billing cycle";
      }
      if (isCustom) {
        const price = form.subscriptionType === "MONTHLY" ? form.monthlyPrice : form.yearlyPrice;
        if (!price || isNaN(parseFloat(price)) || parseFloat(price) < 0)
          errs.price = "A valid price is required for custom plans";
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(s + 1, STEP_COUNT));
  };

  const back = () => setStep((s) => Math.max(s - 1, 1));

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    setSubmitting(true);
    try {
      const planType = effectivePlanId(selectedPlan, form.payment_country);
      const payload = {
        orgName: form.orgName || form.companyName,
        companyName: form.companyName,
        phone: form.phone,
        address: form.address,
        memberName: form.memberName,
        memberEmail: form.memberEmail,
        memberPassword: form.memberPassword,
        planType,
        subscriptionType: form.subscriptionType,
        payment_country: form.payment_country,
      };
      // Always send planMeta so backend can persist full limit fields
      payload.planMeta = form.planMeta;
      if (isCustom) {
        if (form.subscriptionType === "MONTHLY" && form.monthlyPrice)
          payload.monthlyPrice = parseFloat(form.monthlyPrice);
        if (form.subscriptionType === "YEARLY" && form.yearlyPrice)
          payload.yearlyPrice = parseFloat(form.yearlyPrice);
      }
      await createOrganization(payload);
      toast.success("Organization created successfully");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create organization");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Step renders ─────────────────────────────────────────────────────────
  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-gray-800 mb-4">Organization & Owner</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Field label="Organization Name">
              <Input
                placeholder="Leave blank to use company name"
                value={form.orgName}
                onChange={(e) => setField("orgName", e.target.value)}
              />
            </Field>
          </div>
          <Field label="Owner Name" required>
            <Input
              value={form.memberName}
              onChange={(e) => setField("memberName", e.target.value)}
              placeholder="Full name"
            />
            {errors.memberName && <p className="text-xs text-red-500 mt-1">{errors.memberName}</p>}
          </Field>
          <Field label="Owner Email" required>
            <Input
              type="email"
              value={form.memberEmail}
              onChange={(e) => setField("memberEmail", e.target.value)}
              placeholder="owner@example.com"
            />
            {errors.memberEmail && <p className="text-xs text-red-500 mt-1">{errors.memberEmail}</p>}
          </Field>
          <Field label="Password" required>
            <Input
              type="text"
              value={form.memberPassword}
              onChange={(e) => setField("memberPassword", e.target.value)}
              placeholder="Min 6 characters"
            />
            {errors.memberPassword && <p className="text-xs text-red-500 mt-1">{errors.memberPassword}</p>}
          </Field>
          <Field label="Business Location" required>
            <select
              value={form.payment_country}
              onChange={(e) => setField("payment_country", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="US">Outside India — USD (Stripe)</option>
              <option value="IN">Within India — INR (Razorpay)</option>
            </select>
          </Field>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <h3 className="text-base font-semibold text-gray-800 mb-4">Company Details</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Field label="Company Name" required>
            <Input
              value={form.companyName}
              onChange={(e) => setField("companyName", e.target.value)}
              placeholder="e.g. Acme Ltd."
            />
            {errors.companyName && <p className="text-xs text-red-500 mt-1">{errors.companyName}</p>}
          </Field>
        </div>
        <Field label="Phone">
          <PhoneInput
            value={form.phone}
            onChange={(v) => setField("phone", v)}
            placeholder="10-digit number (optional)"
          />
        </Field>
        <Field label="Address">
          <Input
            value={form.address}
            onChange={(e) => setField("address", e.target.value)}
            placeholder="Office address (optional)"
          />
        </Field>
      </div>
    </div>
  );

  const renderStep3 = () => {
    const individualPlans = PLANS_CONFIG.filter((p) => p.section === "individual");
    const groupPlans = PLANS_CONFIG.filter((p) => p.section === "group");

    const PlanCard = ({ plan }) => {
      const active = form.planId === plan.id;
      const price = displayPrice(plan, form.subscriptionType, isIN);
      return (
        <button
          type="button"
          onClick={() => selectPlan(plan.id)}
          className={`relative text-left rounded-xl border-2 p-4 transition-all ${
            active
              ? "border-primary bg-primary/5 shadow-md"
              : "border-gray-200 hover:border-gray-300 bg-white"
          }`}
        >
          {plan.featured && (
            <span className="absolute -top-2.5 left-3 bg-primary text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
              Popular
            </span>
          )}
          {plan.custom && (
            <span className="absolute -top-2.5 left-3 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
              Custom
            </span>
          )}
          <p className={`font-semibold text-sm mb-1 ${active ? "text-primary" : "text-gray-800"}`}>
            {plan.name}
          </p>
          {plan.custom ? (
            <p className="text-xs text-gray-400">Custom pricing</p>
          ) : (
            <p className={`text-lg font-bold ${active ? "text-primary" : "text-gray-900"}`}>
              {currencySymbol}{price}
              <span className="text-xs font-normal text-gray-400">/{cycleLabel}</span>
            </p>
          )}
          <ul className="mt-2 space-y-1">
            {plan.features.map((f) => (
              <li key={f} className="text-[11px] text-gray-500 flex items-start gap-1">
                <span className={active ? "text-primary" : "text-gray-400"}>✓</span> {f}
              </li>
            ))}
          </ul>
          {active && (
            <div className="absolute top-3 right-3 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
                <path d="M10 3L5 8.5 2 5.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              </svg>
            </div>
          )}
        </button>
      );
    };

    return (
      <div className="space-y-6">
        {/* Billing cycle toggle */}
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700">Billing Cycle:</span>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {["MONTHLY", "YEARLY"].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setField("subscriptionType", c)}
                className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                  form.subscriptionType === c
                    ? "bg-primary text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                {c === "MONTHLY" ? "Monthly" : "Yearly"}
                {c === "YEARLY" && <span className="ml-1 text-[10px] opacity-75">Save ~10%</span>}
              </button>
            ))}
          </div>
          <span className="text-xs text-gray-400">
            {isIN ? "Showing INR prices" : "Showing USD prices"}
          </span>
          {errors.subscriptionType && (
            <p className="text-xs text-red-500">{errors.subscriptionType}</p>
          )}
        </div>

        {/* Individual plans */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Individual</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {individualPlans.map((p) => <PlanCard key={p.id} plan={p} />)}
          </div>
        </div>

        {/* Group plans */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Group / Multi-Company</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {groupPlans.map((p) => <PlanCard key={p.id} plan={p} />)}
          </div>
        </div>

        {/* Custom plan pricing + limits */}
        {isCustom && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-4">
            <p className="text-sm font-semibold text-amber-800">Custom Plan Configuration</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {form.subscriptionType === "MONTHLY" ? (
                <Field label={`Monthly Price (${currencySymbol})`} required>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.monthlyPrice}
                    onChange={(e) => setField("monthlyPrice", e.target.value)}
                    placeholder="e.g. 99"
                  />
                </Field>
              ) : (
                <Field label={`Yearly Price (${currencySymbol})`} required>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.yearlyPrice}
                    onChange={(e) => setField("yearlyPrice", e.target.value)}
                    placeholder="e.g. 999"
                  />
                </Field>
              )}
              {errors.price && <p className="md:col-span-2 text-xs text-red-500">{errors.price}</p>}
            </div>
            {/* Numeric limits */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Users (−1 = ∞)">
                <Input
                  type="number"
                  value={form.planMeta.USER_CREATION}
                  onChange={(e) => setPlanMetaField("USER_CREATION", parseInt(e.target.value, 10))}
                />
              </Field>
              <Field label="Sign Requests (−1 = ∞)">
                <Input
                  type="number"
                  value={form.planMeta.SIGN_REQUESTS}
                  onChange={(e) => setPlanMetaField("SIGN_REQUESTS", parseInt(e.target.value, 10))}
                />
              </Field>
              <Field label="Templates (−1 = ∞)">
                <Input
                  type="number"
                  value={form.planMeta.TEMPLATE_CREATION}
                  onChange={(e) => setPlanMetaField("TEMPLATE_CREATION", parseInt(e.target.value, 10))}
                />
              </Field>
              <Field label="Max Companies (−1 = ∞)">
                <Input
                  type="number"
                  value={form.planMeta.ALLOWED_COMPANIES}
                  onChange={(e) => setPlanMetaField("ALLOWED_COMPANIES", parseInt(e.target.value, 10))}
                />
              </Field>
            </div>
            {/* Boolean feature flags */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 pt-1">
              {[
                { key: "BULK_SEND",           label: "Bulk Send" },
                { key: "REMINDERS",           label: "Reminders" },
                { key: "MULTI_DEVICE_ACCESS", label: "Multi-Device Access" },
                { key: "SEQUENTIAL_SIGNING",  label: "Sequential Signing" },
                { key: "ROLE_CONTROL",        label: "Role Control" },
                { key: "ATTACHMENT_REQUESTS", label: "Attachment Requests" },
                { key: "DEDICATED_SUPPORT",   label: "Dedicated Support" },
                { key: "OTP_VERIFICATION",    label: "OTP Verification" },
                { key: "IS_GROUP_PLAN",       label: "Group Plan" },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`pm_${key}`}
                    checked={!!form.planMeta[key]}
                    onChange={(e) => setPlanMetaField(key, e.target.checked)}
                    className="accent-primary"
                  />
                  <label htmlFor={`pm_${key}`} className="text-sm text-gray-700 select-none">{label}</label>
                </div>
              ))}
            </div>
            <p className="text-xs text-amber-700">
              {isIN
                ? "A Razorpay payment link will be generated and emailed to the owner to complete payment."
                : "A Stripe checkout link will be generated and emailed to the owner to complete payment."}
            </p>
          </div>
        )}

        {/* Summary for standard plans */}
        {!isCustom && (
          <div className={`border rounded-xl p-4 text-sm text-gray-700 ${isFreeLocal ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}>
            <span className="font-medium">Selected:</span>{" "}
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${PLAN_COLORS[effectivePlanId(selectedPlan, form.payment_country)] || "bg-gray-100 text-gray-600"}`}>
              {selectedPlan.name}
              {isIN && selectedPlan.idIN ? " (INR)" : " (USD)"}
            </span>
            {" · "}
            {isFreeLocal ? "Free" : `${currencySymbol}${displayPrice(selectedPlan, form.subscriptionType, isIN)}/${cycleLabel}`}
            {" · "}
            {isFreeLocal ? (
              <span className="text-green-600 font-medium">Activated immediately (free plan).</span>
            ) : (
              <span className="text-amber-700 font-medium">
                {isIN ? "A Razorpay payment link" : "A Stripe checkout link"} will be sent to the owner to complete payment.
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderReview = () => {
    const plan = selectedPlan;
    const planType = effectivePlanId(plan, form.payment_country);
    const price = isCustom
      ? (form.subscriptionType === "MONTHLY" ? form.monthlyPrice : form.yearlyPrice)
      : displayPrice(plan, form.subscriptionType, isIN);
    return (
      <div className="space-y-4 text-sm">
        <h3 className="font-semibold text-gray-800 text-base">Review before creating</h3>
        <div className="grid grid-cols-2 gap-3">
          {[
            ["Organization", form.orgName || form.companyName],
            ["Owner Name", form.memberName],
            ["Owner Email", form.memberEmail],
            ["Company", form.companyName],
            ["Location", form.payment_country === "IN" ? "India (INR)" : "Outside India (USD)"],
            ["Plan", plan.name + (isIN && plan.idIN ? " (IN)" : "")],
            ["Billing", form.subscriptionType === "MONTHLY" ? "Monthly" : "Yearly"],
            ["Price", price != null ? `${currencySymbol}${price}/${cycleLabel}` : "Custom"],
            ["Status", isFreeLocal ? "Active on creation" : "Pending payment"],
          ].map(([label, value]) => (
            <div key={label} className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-400 mb-0.5">{label}</p>
              <p className="font-medium text-gray-800 truncate">{value}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-stretch">
      <div className="flex-1 flex flex-col bg-white overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b bg-white shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Create Organization</h2>
            <p className="text-sm text-gray-500">Set up a new organization with owner, company, and plan details.</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-8">
          <div className="max-w-4xl mx-auto">
            <StepIndicator current={step} total={STEP_COUNT} labels={STEPS} />
            <div className="min-h-[400px]">
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && (
                <div className="space-y-8">
                  {renderStep3()}
                  <div className="border-t pt-6">{renderReview()}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t bg-gray-50 flex items-center justify-between shrink-0">
          <button
            onClick={step === 1 ? onClose : back}
            className="px-5 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {step === 1 ? "Cancel" : "← Back"}
          </button>
          {step < STEP_COUNT ? (
            <button
              onClick={next}
              className="px-6 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors"
            >
              Continue →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-6 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {submitting && (
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {submitting ? "Creating…" : "Create Organization"}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Organizations Page ────────────────────────────────────────────────────────
const Organizations = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [paymentCountry, setPaymentCountry] = useState(""); // "" | "US" | "IN"
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["organizations", page, search, paymentCountry],
    queryFn: () =>
      getOrganizations({
        page,
        limit: 10,
        search,
        ...(paymentCountry && { payment_country: paymentCountry }),
      }).then((r) => r.data),
    keepPreviousData: true,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteOrganization,
    onSuccess: () => {
      toast.success("Organization deleted");
      queryClient.invalidateQueries(["organizations"]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to delete organization");
    },
  });

  const handleDelete = (orgId, orgName) => {
    if (window.confirm(`Are you sure you want to delete "${orgName}"? This cannot be undone.`)) {
      deleteMutation.mutate(orgId);
    }
  };

  const organizations = data?.data || [];
  const pagination = data?.pagination || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organizations</h1>
          <p className="text-gray-500 text-sm mt-1">Manage organizations, their companies, and billing plans.</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          + Create Organization
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search organizations..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-72 focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <select
          value={paymentCountry}
          onChange={(e) => { setPaymentCountry(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
        >
          <option value="">All regions</option>
          <option value="US">US (Stripe / USD)</option>
          <option value="IN">IN (Razorpay / INR)</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {["Organization", "Owner", "Companies", "Plan", "Status", "Created", "Actions"].map((h) => (
                <th key={h} className="px-5 py-3 text-left font-medium text-gray-500 uppercase tracking-wider text-xs">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="text-center py-10 text-gray-400">Loading...</td>
              </tr>
            ) : organizations.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-10 text-gray-400">No organizations found</td>
              </tr>
            ) : (
              organizations.map((org) => (
                <tr key={org._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4">
                    <p className="font-medium text-gray-900">{org.name}</p>
                    <p className="text-xs text-gray-400">{org._id}</p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-gray-800">{org.ownerId?.name || "—"}</p>
                    <p className="text-xs text-gray-400">{org.ownerId?.email}</p>
                  </td>
                  <td className="px-5 py-4 text-center">
                    <span className="font-medium text-gray-700">{org.companyCount || 0}</span>
                    {org.allowedCompanies !== -1 && (
                      <span className="text-gray-400"> / {org.allowedCompanies}</span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${PLAN_COLORS[org.planId] || "bg-gray-100 text-gray-600"}`}>
                      {PLAN_LABELS[org.planId] || org.planId || "—"}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${org.isSubscribed ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                      {org.isSubscribed ? "Active" : "Inactive"}
                    </span>
                    {org.lastPaymentFailed && (
                      <span className="ml-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        Payment Failed
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-gray-500">
                    {org.createdAt ? new Date(org.createdAt).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-5 py-4 space-x-2">
                    <button
                      onClick={() => navigate(`/dashboard/organizations/${org._id}`)}
                      className="text-primary hover:text-primary/80 text-xs font-medium"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleDelete(org._id, org.name)}
                      className="text-red-500 hover:text-red-700 text-xs font-medium"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>

        {/* Pagination - same UI as Companies */}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-b-xl">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= (pagination.totalPages || 1)}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing{" "}
              <span className="font-medium">{(page - 1) * 10 + 1}</span> to{" "}
              <span className="font-medium">
                {Math.min(page * 10, pagination.total || 0)}
              </span>{" "}
              of <span className="font-medium">{pagination.total || 0}</span> results
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {[...Array(Math.max(1, pagination.totalPages || 1))].map((_, index) => (
                <button
                  key={index + 1}
                  onClick={() => setPage(index + 1)}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                    page === index + 1
                      ? "z-10 bg-primary border-primary text-white"
                      : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  {index + 1}
                </button>
              ))}
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= (pagination.totalPages || 1)}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </nav>
          </div>
        </div>
      </div>
      </div>

      {/* Full-screen Create Modal */}
      {showCreateModal && (
        <CreateOrgModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            queryClient.invalidateQueries(["organizations"]);
          }}
        />
      )}
    </div>
  );
};

export default Organizations;
